import * as React from "react";
import styled, { css } from "styled-components";
import * as moment from "moment-timezone";
import _ from "lodash";

import Grid from "src/library/components/Hedron";
import {
  Text,
  TextTruncate
} from "src/library/components/Text";
import { breakpoint } from "src/library/utils/styled-media-queries/index";
import { InstalledModuleType } from "src/utils/constants";
import Price from "src/core/prices/components/Price";
import { getOrderMode } from "src/core/drops/utils/inherited-settings";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import BrandedImage from "src/core/shared/components/BrandedImage";
import { canListOrderProduct } from "src/core/orders/utils/orders";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { IDrop } from "src/core/drops/api/drops.api";
import { ISchedule } from "src/core/schedules/api/schedules.api";
import { ProductStatus } from "src/core/products/api/products.api";
import { useIsMobile } from "src/library/hooks";
import { useQuickViewProduct } from "src/core/products/components/QuickViewProduct";
import { ProductQuantityControls } from "src/core/products/components/ProductDetails/ProductQuantityControls";

import { IListLine } from "../api/lists.api";

interface IOrderProductLine {
  mode: "edit" | "view";
  listLine: IListLine;
  drop: IDrop;
  installedModules: InstalledModuleType[];
  schedule?: ISchedule | null;
}

const ListProductLine: React.ReactFCWithChildren<IOrderProductLine> = ({
  listLine, mode, drop, installedModules, schedule
}) => {
  const pricesVisible = usePriceVisibility();
  let lineMessage = "";
  const orderMode = getOrderMode(drop.groupedSettings);
  const listLineError = React.useMemo(() => canListOrderProduct(listLine.product, schedule), [ schedule, listLine ]);
  const isMobile = useIsMobile();
  const { setQuickViewProduct } = useQuickViewProduct();
  const productCurrentStatus = listLine.product.currentStatus;

  const listLineWarning = React.useMemo(() => {
    if (listLine?.product?.prices?.length) {
      const currentPrice = listLine.product.prices[ 0 ];

      if (currentPrice && moment.utc(currentPrice.effectiveDate).isAfter(moment.utc())) {
        return `Product is not available until ${moment.utc(currentPrice.effectiveDate).format("DD/MM/YYYY")}`;
      }
    }

    if (productCurrentStatus?.status === ProductStatus.Suspended) {
      return `Product is not available between ${moment.utc(productCurrentStatus.startDate).format("DD/MM/YYYY")} and ${moment.utc(productCurrentStatus.endDate).format("DD/MM/YYYY")}`;
    }

    return null;
  }, [ listLine.product.prices, productCurrentStatus ]);

  if (listLine.product) {
    if (listLine.product.madeToOrder && orderMode && orderMode.allow !== "MTO") {
      lineMessage = "Made to order";
    }

    if (productCurrentStatus?.status === ProductStatus.Delisted) {
      lineMessage = _.capitalize(productCurrentStatus?.status);
    }
  }

  return (
    <>
      <ListLineWrapper
        direction="horizontal"
        valign="center"
        className={`${listLineError ? "error" : listLineWarning ? "warn editable" : "editable"}`}
      >
        <Grid.Box className="image-wrapper">
          <BrandedImage
            s3Key={listLine.product && listLine.product.image}
            alt={listLine.product && listLine.product.description}
            level="public"
          />
        </Grid.Box>

        <Grid.Bounds
          className="product-details"
          direction="vertical"
        >
          <div
            onClick={() => setQuickViewProduct(listLine.productId)}
            className="link-text"
          >
            <TextTruncate
              as="h3"
              truncateProps={{ lines: isMobile ? 1 : 2 }}
            >
              {listLine.product.description}
            </TextTruncate>

            <br />

            <Text
              color="secondary"
              weight="bold"
              textTransform="uppercase"
              letterSpacing="big"
              className="part-number"
            >
              {listLine.product.partNumber}
            </Text>
          </div>

          <Grid.Box>
            {drop && pricesVisible && (
              <Text
                fontSize="h3"
                weight="bold"
                textTransform="none"
              >
                <Price
                  mode={mode}
                  line={listLine}
                  product={listLine.product ? listLine.product : null}
                  installedModules={installedModules}
                  quantity={listLine.quantity}
                  type="unit"
                  usePackSizeMultiplier={mode === "edit"}
                />
              </Text>
            )}

            {lineMessage && (
              <Text
                className="line-message"
                fontSize="h4"
              >
                {lineMessage}
              </Text>
            )}
          </Grid.Box>
        </Grid.Bounds>

        <div className="quant-total">
          <Grid.Box
            className={`quantity ${mode}`}
            valign="center"
            shiftRight
          >
            {mode === "edit" ? (
              <ProductQuantityControls
                product={listLine.product}
                orderable={!listLineError}
              />
            ) : (
              <Text>
                {listLine.quantity}
              </Text>
            )}
          </Grid.Box>

          {drop && pricesVisible && (
            <Grid.Box
              className="total"
              shiftRight
            >
              <Text
                fontSize="h3"
                weight="bold"
              >
                <Price
                  mode={mode}
                  line={listLine}
                  product={listLine.product ? listLine.product : null}
                  installedModules={installedModules}
                  quantity={listLine.quantity}
                  type="total"
                />
              </Text>
            </Grid.Box>
          )}
        </div>
      </ListLineWrapper>

      <ErrorNotice
        message={listLineError}
        type="error"
      />

      {!listLineError && (
        <ErrorNotice
          message={listLineWarning}
          type="warn"
        />
      )}
    </>
  );
};

const ListLineWrapper = styled(Grid.Bounds)`
${({ theme }) => css`
  background: ${theme.colors.white};
  margin-bottom: 0;
  padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.sm};
  min-height: 150px;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: wrap;
  padding-top: 70px;
  position: relative;
  border-top: 1px solid ${theme.colors.lightGrey};

  h3 {
    font-size: ${theme.fonts.sizes.h4};
  }

  .total-price,
  .current-price {
    font-size: calc(${theme.fonts.sizes.h3});
  }

  .image-wrapper {
    display: none;
    position: relative;
    background-color: ${theme.colors.lightGrey};
    width: 80px;
    height: 80px;
    overflow: hidden;
    flex-shrink: 0;
    > img {
      width: auto;
      height: auto;
      object-fit: contain;
      position: absolute;
      transform: translate(-50%, -50%);
      top: 50%;
      left: 50%;
      max-height: 100%;
    }
    > img.placeholder {
      width: 30px;
    }
  }

  .product-details {
    align-self: flex-start;

    .part-number-wrapper {
      min-width: 110px;
    }

    .link-text {
      position: absolute;
      width: 100%;
      top: ${theme.spacing.padding.sm};
      left: 0;
      padding: 0 ${theme.spacing.padding.sm};
      cursor: pointer;
    }

    h3 {
      margin-bottom: 0px;
    }
  }

  .quant-total {
    display: flex;
    flex-direction: row;
    height: 100%;
    align-self: flex-start;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    flex-wrap: wrap;

    .quantity {
      padding-right: ${theme.spacing.padding.md};
      display: flex;
      align-items: center;
      margin: initial;
      order: 2;
      
      &.view span {
        font-size: ${theme.fonts.sizes.h4};
      }

      &.view span:before {
        content: "QTY";
        display: inline-block;
        margin-right: 5px;
        color: ${theme.fonts.colors.secondary};
      }
    }

    .total {
      padding-right: ${theme.spacing.padding.md};
      margin-right: ${theme.spacing.padding.md};
      margin-left: 0;
      order: 1;

      .current-price-wrapper {
        align-self: flex-start;

        .vat-wrapper {
          display: block;
          margin-left: 0;
        }

        .current-price {
          font-size: ${theme.fonts.sizes.headingScale};
        }
      }

      & > div {
        align-items: flex-end;
      }
    }

    .pack-size-selector-wrapper, .order-control-wrapper {
      display: flex;
      justify-content: flex-end;
    }
  }

  &.error {
    background: ${theme.colors.errorLight};
  }

  &.disabled {
    opacity: 0.5;

    .quant-total {
      .quantity {
        margin-top: 5px;
      }
    }
  }

  .quant-total {
    justify-content: space-between;
  }



  ${breakpoint("sm", theme)} {
    flex-direction: row;

    .product-details {
      min-width: 130px;
    }

    .quant-total {
      justify-content: flex-end;
      align-self: center;
      padding: 0;      
      
      .total {
        padding-right: 0;
        margin-top: 5px;

        .current-price-wrapper {
          align-self: initial;
        }
      }
    }
  }

  ${breakpoint("md", theme)} {
    justify-content: flex-start;
    padding-left: 0;
    padding: 0;
    height: auto;
    border-top: 0;
    margin-bottom: ${theme.spacing.margins.sm};
    
    .product-details {
      padding: ${theme.spacing.padding.sm} 0;

      .link-text {
        position: inherit;
        top: inherit;
        left: inherit;
        padding: 0;
      }
    }

    .image-wrapper {
      display: block;
      margin-right: ${theme.spacing.margins.sm};
    }

    .quant-total {

      .total {
        padding-right: ${theme.spacing.padding.sm};
      }
    }
  }

  ${breakpoint("lg", theme)} {
    padding: 0 ${theme.spacing.padding.sm};
    
    .quant-total {
      justify-content: flex-end;
    }
  }


  ${breakpoint("xxl", theme)} {
    justify-content: flex-start;
    padding-left: 0;
    padding-right: ${theme.spacing.padding.md};
    
    .image-wrapper {
      display: block;
      margin-right: ${theme.spacing.margins.md};

      width: 120px;
      height: 120px;
      > img.placeholder {
        width: 30px;
      }
    }

    .quant-total {
      flex-direction: row;
      flex-wrap: nowrap;
      min-width: 300px;
      width: 300px;
      margin-left: auto;

      .quantity {
        padding-right: 0;
      }
    }

    h3,
    .total-price,
    .current-price {
      font-size: ${theme.fonts.sizes.h4};
    }
  }

  ${breakpoint("xxl", theme)} {
    .product-details {
      padding-right: ${theme.spacing.padding.lg};
    }

    h3,
    .total-price,
    .current-price {
      font-size: ${theme.fonts.sizes.h4};
    }
  }

`};
`;

export default ListProductLine;
